import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #mediaScrollContainer = null;
  /** @type {number | null} */
  #scrollSyncRaf = null;
  /** @type {number | null} */
  #lastScrollLeft = null;
  /** @type {number | null} */
  #lastSyncedSlideIndex = null;
  /** @type {number | null} */
  #touchStartX = null;
  /** @type {number | null} */
  #touchStartY = null;
  /** @type {number | null} */
  #touchStartSlideIndex = null;
  /** @type {number | null} */
  #touchStartScrollLeft = null;
  /** @type {number | null} */
  #touchStartMaxScrollLeft = null;
  /** @type {Animation | null} */
  #swipeAnimation = null;
  /** @type {number} */
  #ignoreScrollSyncUntil = 0;
  /** @type {number | null} */
  #activeAutoplaySlideIndex = null;
  /** @type {boolean} */
  #scrollSyncFramePending = false;

  #animateSwipeSelection(slideIndex, direction = 'next') {
    if (prefersReducedMotion()) return;
    const mediaItem = this.#findMediaBySlideIndex(slideIndex, true) || this.#findMediaBySlideIndex(slideIndex, false);
    if (!(mediaItem instanceof HTMLElement)) return;
    this.#setLoadingStateForMedia(mediaItem);
    const media = mediaItem.querySelector('.product-media');
    const target = media instanceof HTMLElement ? media : mediaItem;
    const fromX = direction === 'prev' ? '-12px' : '12px';

    if (typeof target.animate === 'function') {
      try {
        this.#swipeAnimation?.cancel();
      } catch {}
      this.#swipeAnimation = target.animate(
        [
          { opacity: 0.9, transform: `translate3d(${fromX}, 0, 0) scale(0.997)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
        ],
        {
          duration: 620,
          easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
        }
      );
      this.#swipeAnimation.addEventListener(
        'finish',
        () => {
          this.#swipeAnimation = null;
        },
        { once: true }
      );
      return;
    }

    // Fallback for environments without Web Animations API.
    target.classList.remove('hz-media-swipe-animate');
    target.classList.remove('hz-media-swipe-next', 'hz-media-swipe-prev');
    void target.offsetWidth;
    target.classList.add(direction === 'prev' ? 'hz-media-swipe-prev' : 'hz-media-swipe-next');
    target.classList.add('hz-media-swipe-animate');
    target.addEventListener(
      'animationend',
      () => {
        target.classList.remove('hz-media-swipe-animate');
        target.classList.remove('hz-media-swipe-next', 'hz-media-swipe-prev');
      },
      { once: true }
    );
  }

  #setLoadingStateForMedia(mediaItem) {
    if (!(mediaItem instanceof HTMLElement)) return;
    const media = mediaItem.querySelector('.product-media');
    const target = media instanceof HTMLElement ? media : mediaItem;
    const img = mediaItem.querySelector('img');
    if (!(img instanceof HTMLImageElement)) {
      target.classList.remove('hz-media-is-loading');
      return;
    }
    if (img.complete && img.naturalWidth > 0) {
      target.classList.remove('hz-media-is-loading');
      return;
    }

    target.classList.add('hz-media-is-loading');
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      target.classList.remove('hz-media-is-loading');
    };
    img.addEventListener('load', release, { once: true });
    img.addEventListener('error', release, { once: true });
    setTimeout(release, 4500);
  }

  #alignDesktopMediaToViewportStart(mediaContainer) {
    if (!(mediaContainer instanceof HTMLElement)) return;
    if (window.matchMedia('(max-width: 749px)').matches) return;
    if (!(this.#mediaScrollContainer instanceof HTMLElement)) return;

    const targetTop = mediaContainer.offsetTop;
    if (!Number.isFinite(targetTop)) return;
    if (Math.abs(this.#mediaScrollContainer.scrollTop - targetTop) <= 1) return;

    this.#mediaScrollContainer.scrollTo({ top: targetTop, behavior: 'instant' });
  }

  /**
   * Autoplay media if it's a video in the zoom dialog.
   * @param {HTMLElement | undefined} mediaContainer
   */
  #autoplayMedia(mediaContainer) {
    if (!mediaContainer) return;
    const slideIndex = parseInt(mediaContainer.getAttribute('data-slide-index') || '', 10);
    const isSameSlide =
      Number.isFinite(slideIndex) && Number.isFinite(this.#activeAutoplaySlideIndex)
        ? slideIndex === this.#activeAutoplaySlideIndex
        : false;
    const deferred = mediaContainer.querySelector('deferred-media');
    if (deferred) {
      try {
        if (deferred.hasAttribute('data-media-loaded')) {
          if (typeof deferred.playMedia === 'function') deferred.playMedia();
        } else if (typeof deferred.loadContent === 'function') {
          deferred.loadContent(false);
          // Retry after content is injected (Safari needs the element present).
          setTimeout(() => this.#autoplayMedia(mediaContainer), 200);
          return;
        } else if (window.customElements?.whenDefined) {
          window.customElements
            .whenDefined('deferred-media')
            .then(() => setTimeout(() => this.#autoplayMedia(mediaContainer), 0))
            .catch(() => {});
          return;
        } else {
          setTimeout(() => this.#autoplayMedia(mediaContainer), 200);
          return;
        }
      } catch {}
    }
    const video = mediaContainer.querySelector('video');
    if (video) {
      if (isSameSlide && !video.paused) return;
      const alignViewport = () => this.#alignDesktopMediaToViewportStart(mediaContainer);
      video.addEventListener('loadedmetadata', alignViewport, { once: true });
      video.addEventListener('loadeddata', alignViewport, { once: true });

      if (video.muted !== true) video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.playsInline = true;
      try {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch {}
      setTimeout(() => {
        if (!video.isConnected || !video.paused) return;
        try {
          const retryPromise = video.play();
          if (retryPromise && typeof retryPromise.catch === 'function') {
            retryPromise.catch(() => {});
          }
        } catch {}
      }, 140);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => alignViewport());
      });

      if (Number.isFinite(slideIndex)) {
        this.#activeAutoplaySlideIndex = slideIndex;
      }
    }
  }

  #pauseMedia(mediaContainer) {
    if (!(mediaContainer instanceof HTMLElement)) return;
    const video = mediaContainer.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.paused) {
      try {
        video.pause();
      } catch {}
    }
  }

  #pauseInactiveMedia(activeSlideIndex) {
    this.refs.media.forEach((mediaItem, fallbackIndex) => {
      const mediaIndex = this.#getSlideIndexFromElement(mediaItem, fallbackIndex);
      if (mediaIndex !== activeSlideIndex) this.#pauseMedia(mediaItem);
    });
  }

  #syncThumbnailSelection(targetIndex) {
    const { thumbnails } = this.refs;
    if (!thumbnails || !thumbnails.children.length) return;
    const target = Number(targetIndex);
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      const dataIndex = parseInt(button.getAttribute('data-slide-index') || '', 10);
      const isSelected =
        Number.isFinite(target) && (Number.isFinite(dataIndex) ? dataIndex === target : i === target);
      button.setAttribute('aria-selected', `${isSelected}`);
    });
  }

  #suppressScrollSync(ms = 260) {
    this.#ignoreScrollSyncUntil = performance.now() + Math.max(0, ms);
  }

  #scrollThumbnailIntoView(slideIndex, behavior = 'smooth') {
    const targetThumbnail = this.#findThumbnailBySlideIndex(slideIndex, true) || this.#findThumbnailBySlideIndex(slideIndex);
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement) || !this.refs.thumbnails) return;
    scrollIntoView(targetThumbnail, {
      ancestor: this.refs.thumbnails,
      behavior,
      block: 'nearest',
      inline: 'center',
    });
  }

  #applyLoopTransitionHint() {
    if (prefersReducedMotion()) return;
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;
    dialog.classList.remove('hz-zoom-looping');
    requestAnimationFrame(() => {
      dialog.classList.add('hz-zoom-looping');
      requestAnimationFrame(() => dialog.classList.remove('hz-zoom-looping'));
    });
  }

  #isElementVisible(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.hasAttribute('hidden')) return false;
    const styles = getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  #getVisibleMedia() {
    return this.refs.media.filter((item) => this.#isElementVisible(item));
  }

  #getSlideIndexFromElement(element, fallback = 0) {
    const dataIndex = parseInt(element?.getAttribute('data-slide-index') || '', 10);
    if (Number.isFinite(dataIndex)) return dataIndex;
    return fallback;
  }

  #findMediaBySlideIndex(slideIndex, visibleOnly = false) {
    const target = Number(slideIndex);
    if (!Number.isFinite(target)) return undefined;

    const media = visibleOnly ? this.#getVisibleMedia() : this.refs.media;
    const exact = media.find((item) => this.#getSlideIndexFromElement(item, Number.NaN) === target);
    if (exact) return exact;

    if (!visibleOnly && media[target]) return media[target];
    return undefined;
  }

  #findThumbnailBySlideIndex(slideIndex, visibleOnly = false) {
    const { thumbnails } = this.refs;
    if (!thumbnails) return undefined;
    const target = Number(slideIndex);
    if (!Number.isFinite(target)) return undefined;

    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    return buttons.find((button, i) => {
      if (visibleOnly && !this.#isElementVisible(button)) return false;
      const dataIndex = parseInt(button.getAttribute('data-slide-index') || '', 10);
      return Number.isFinite(dataIndex) ? dataIndex === target : i === target;
    });
  }

  #resolveActiveMediaByScrollPosition() {
    const container = this.#mediaScrollContainer;
    if (!container) return undefined;

    const visibleMedia = this.#getVisibleMedia();
    if (!visibleMedia.length) return undefined;

    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    if (maxScrollTop > 0) {
      if (container.scrollTop <= 2) return visibleMedia[0];
      if (container.scrollTop >= maxScrollTop - 2) return visibleMedia[visibleMedia.length - 1];
    }

    const containerRect = container.getBoundingClientRect();
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const containerCenterY = containerRect.top + containerRect.height / 2;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (!isMobile) {
      const viewportMid = container.scrollTop + container.clientHeight / 2;
      let winner = visibleMedia[0];
      let minDistance = Number.POSITIVE_INFINITY;
      visibleMedia.forEach((item) => {
        const itemMid = item.offsetTop + item.offsetHeight / 2;
        const distance = Math.abs(itemMid - viewportMid);
        if (distance < minDistance) {
          minDistance = distance;
          winner = item;
        }
      });
      return winner;
    }

    const minimumActiveRatio = isMobile ? 0.35 : 0.5;

    let winner = visibleMedia[0];
    let bestVisibilityRatio = Number.NEGATIVE_INFINITY;
    let bestCenterPenalty = Number.POSITIVE_INFINITY;

    visibleMedia.forEach((item) => {
      const rect = item.getBoundingClientRect();
      const overlapX = Math.max(0, Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left));
      const overlapY = Math.max(0, Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top));
      const visibleArea = overlapX * overlapY;
      const itemArea = Math.max(1, rect.width * rect.height);
      const visibilityRatio = visibleArea / itemArea;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const centerPenalty = Math.abs(centerX - containerCenterX) + Math.abs(centerY - containerCenterY);
      if (
        visibilityRatio > bestVisibilityRatio + 0.001 ||
        (Math.abs(visibilityRatio - bestVisibilityRatio) <= 0.001 && centerPenalty < bestCenterPenalty)
      ) {
        bestVisibilityRatio = visibilityRatio;
        bestCenterPenalty = centerPenalty;
        winner = item;
      }
    });

    if (isMobile && bestVisibilityRatio < minimumActiveRatio) {
      const selectedButton = this.refs.thumbnails?.querySelector('button[aria-selected="true"]:not([hidden])');
      const selectedIndex = parseInt(selectedButton?.getAttribute('data-slide-index') || '', 10);
      const selectedMedia = this.#findMediaBySlideIndex(selectedIndex, true) || this.#findMediaBySlideIndex(selectedIndex, false);
      if (selectedMedia) return selectedMedia;
    }

    return winner;
  }

  #syncFromScrollPosition() {
    if (performance.now() < this.#ignoreScrollSyncUntil) return;
    const activeMedia = this.#resolveActiveMediaByScrollPosition() || this.#getVisibleMedia()[0] || this.refs.media[0];
    if (!activeMedia) return;

    const fallbackIndex = this.refs.media.indexOf(activeMedia);
    const activeSlideIndex = this.#getSlideIndexFromElement(activeMedia, fallbackIndex);
    if (!Number.isFinite(activeSlideIndex)) return;

    if (this.#lastSyncedSlideIndex === activeSlideIndex) return;

    this.#syncThumbnailSelection(activeSlideIndex);
    this.#loadHighResolutionImage(activeMedia);
    this.#setLoadingStateForMedia(activeMedia);
    this.#autoplayMedia(activeMedia);
    this.#pauseInactiveMedia(activeSlideIndex);
    this.#scrollThumbnailIntoView(activeSlideIndex, prefersReducedMotion() ? 'instant' : 'smooth');
    requestAnimationFrame(() => this.#scrollThumbnailIntoView(activeSlideIndex, 'instant'));
    this.#lastSyncedSlideIndex = activeSlideIndex;
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeSlideIndex));
  }

  #getVisibleSlideIndices() {
    return this.#getVisibleMedia()
      .map((item, i) => this.#getSlideIndexFromElement(item, i))
      .filter((idx) => Number.isFinite(idx));
  }

  #resolveActiveSlideIndex() {
    const activeMedia = this.#resolveActiveMediaByScrollPosition() || this.#getVisibleMedia()[0];
    if (activeMedia) {
      const fallbackIndex = this.refs.media.indexOf(activeMedia);
      return this.#getSlideIndexFromElement(activeMedia, fallbackIndex);
    }
    const selectedButton = this.refs.thumbnails?.querySelector('button[aria-selected="true"]');
    const dataIndex = parseInt(selectedButton?.getAttribute('data-slide-index') || '', 10);
    return Number.isFinite(dataIndex) ? dataIndex : Number.NaN;
  }

  #handleMediaTouchStart = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    const selectedSlideIndex = parseInt(
      this.refs.thumbnails?.querySelector('button[aria-selected="true"]:not([hidden])')?.getAttribute('data-slide-index') || '',
      10
    );
    const resolvedSlideIndex = Number.isFinite(selectedSlideIndex)
      ? selectedSlideIndex
      : Number.isFinite(this.#lastSyncedSlideIndex)
        ? this.#lastSyncedSlideIndex
        : this.#resolveActiveSlideIndex();
    this.#touchStartSlideIndex = Number.isFinite(resolvedSlideIndex) ? resolvedSlideIndex : null;
    if (this.#mediaScrollContainer instanceof HTMLElement) {
      this.#touchStartScrollLeft = this.#mediaScrollContainer.scrollLeft;
      this.#touchStartMaxScrollLeft = Math.max(0, this.#mediaScrollContainer.scrollWidth - this.#mediaScrollContainer.clientWidth);
    } else {
      this.#touchStartScrollLeft = null;
      this.#touchStartMaxScrollLeft = null;
    }
  };

  #handleMediaTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const startX = this.#touchStartX;
    const startY = this.#touchStartY;
    const startSlideIndex = this.#touchStartSlideIndex;
    this.#touchStartX = null;
    this.#touchStartY = null;
    this.#touchStartSlideIndex = null;
    this.#touchStartScrollLeft = null;
    this.#touchStartMaxScrollLeft = null;

    if (!Number.isFinite(startX) || !Number.isFinite(startY) || !Number.isFinite(startSlideIndex)) return;

    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;
    if (Math.abs(dx) < 28 || Math.abs(dx) < Math.abs(dy)) return;
    const swipedTowardsNext = dx < 0;
    const swipedTowardsPrevious = dx > 0;

    const visibleIndices = this.#getVisibleSlideIndices();
    if (visibleIndices.length < 2) return;
    const edgeLastPos = visibleIndices.length - 1;
    const startPos = visibleIndices.indexOf(startSlideIndex);
    if (startPos < 0) return;
    const step = swipedTowardsNext ? 1 : -1;
    const targetPos = (startPos + step + visibleIndices.length) % visibleIndices.length;
    const targetSlideIndex = visibleIndices[targetPos];
    const wrapped =
      (swipedTowardsNext && startPos === edgeLastPos && targetPos === 0) ||
      (swipedTowardsPrevious && startPos === 0 && targetPos === edgeLastPos);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const settledSlideIndex = this.#resolveActiveSlideIndex();
        const settledPos = visibleIndices.indexOf(settledSlideIndex);
        if (settledPos !== targetPos) {
          if (wrapped) this.#applyLoopTransitionHint();
          this.selectThumbnail(targetSlideIndex, { behavior: 'instant' });
          this.#animateSwipeSelection(targetSlideIndex, swipedTowardsNext ? 'next' : 'prev');
          requestAnimationFrame(() => this.#syncFromScrollPosition());
          return;
        }
        this.#syncThumbnailSelection(targetSlideIndex);
        this.#animateSwipeSelection(targetSlideIndex, swipedTowardsNext ? 'next' : 'prev');
        requestAnimationFrame(() => this.#scrollThumbnailIntoView(targetSlideIndex, 'instant'));
        requestAnimationFrame(() => this.#syncFromScrollPosition());
      });
    });
  };

  #stopScrollSyncLoop() {
    if (this.#scrollSyncRaf != null) {
      cancelAnimationFrame(this.#scrollSyncRaf);
      this.#scrollSyncRaf = null;
    }
  }

  #startScrollSyncLoop() {
    this.#stopScrollSyncLoop();
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const tick = () => {
      if (!this.refs.dialog?.open) {
        this.#scrollSyncRaf = null;
        return;
      }

      const container = this.#mediaScrollContainer;
      const currentScrollLeft = container ? Math.round(container.scrollLeft) : null;
      this.#lastScrollLeft = currentScrollLeft;
      // Always sync while dialog is open; iOS momentum scrolling can settle without
      // emitting a final scroll event that matches the selected thumbnail state.
      this.#syncFromScrollPosition();

      this.#scrollSyncRaf = requestAnimationFrame(tick);
    };

    this.#scrollSyncRaf = requestAnimationFrame(tick);
  }

  #bindMediaScrollContainer() {
    const nextContainer = this.refs.media?.[0]?.parentElement;
    if (!(nextContainer instanceof HTMLElement)) return;
    if (this.#mediaScrollContainer === nextContainer) return;

    this.#mediaScrollContainer?.removeEventListener('scroll', this.handleScroll);
    this.#mediaScrollContainer?.removeEventListener('touchstart', this.#handleMediaTouchStart);
    this.#mediaScrollContainer?.removeEventListener('touchend', this.#handleMediaTouchEnd);
    this.#mediaScrollContainer?.removeEventListener('touchcancel', this.#handleMediaTouchEnd);
    this.#mediaScrollContainer = nextContainer;
    this.#mediaScrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });
    this.#mediaScrollContainer.addEventListener('touchstart', this.#handleMediaTouchStart, { passive: true });
    this.#mediaScrollContainer.addEventListener('touchend', this.#handleMediaTouchEnd, { passive: true });
    this.#mediaScrollContainer.addEventListener('touchcancel', this.#handleMediaTouchEnd, { passive: true });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#bindMediaScrollContainer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaScrollContainer?.removeEventListener('touchstart', this.#handleMediaTouchStart);
    this.#mediaScrollContainer?.removeEventListener('touchend', this.#handleMediaTouchEnd);
    this.#mediaScrollContainer?.removeEventListener('touchcancel', this.#handleMediaTouchEnd);
    this.#mediaScrollContainer?.removeEventListener('scroll', this.handleScroll);
    this.#mediaScrollContainer = null;
    this.#stopScrollSyncLoop();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();
    this.#bindMediaScrollContainer();

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    const sourceSlideIndex = parseInt(sourceImage?.getAttribute('data-slide-index') || '', 10);

    const { dialog, media, thumbnails } = this.refs;
    const requestedIndex = Number(index);
    const effectiveIndex = Number.isFinite(sourceSlideIndex) ? sourceSlideIndex : requestedIndex;
    const targetImage =
      this.#findMediaBySlideIndex(effectiveIndex, true) ||
      this.#findMediaBySlideIndex(effectiveIndex, false) ||
      this.#getVisibleMedia()[0];
    const targetSlideIndex = targetImage
      ? this.#getSlideIndexFromElement(targetImage, effectiveIndex)
      : effectiveIndex;
    const targetThumbnail = this.#findThumbnailBySlideIndex(targetSlideIndex, true) || thumbnails.children[0];
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    const open = () => {
      dialog.showModal();
      this.#suppressScrollSync(320);

      targetThumbnail?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
      targetImage?.scrollIntoView({
        behavior: 'instant',
        block: isMobile ? 'center' : 'start',
        inline: isMobile ? 'center' : 'nearest',
      });
      this.#syncThumbnailSelection(targetSlideIndex);
      this.#autoplayMedia(targetImage);
      this.#pauseInactiveMedia(targetSlideIndex);
      this.dispatchEvent(new ZoomMediaSelectedEvent(targetSlideIndex));
      this.#lastSyncedSlideIndex = targetSlideIndex;
      this.#lastScrollLeft = this.#mediaScrollContainer ? Math.round(this.#mediaScrollContainer.scrollLeft) : null;
      this.#startScrollSyncLoop();
      setTimeout(() => this.handleScroll(), 80);
    };

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(targetSlideIndex, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = () => {
    if (this.#scrollSyncFramePending) return;
    this.#scrollSyncFramePending = true;
    requestAnimationFrame(() => {
      this.#scrollSyncFramePending = false;
      this.#syncFromScrollPosition();
    });
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Resolve active media using current horizontal position first (mobile-safe).
    const mostVisibleElement = this.#resolveActiveMediaByScrollPosition() || this.#getVisibleMedia()[0] || media[0];
    if (!mostVisibleElement) return this.closeDialog();

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#stopScrollSyncLoop();
    this.refs.media.forEach((mediaItem) => this.#pauseMedia(mediaItem));
    this.#activeAutoplaySlideIndex = null;
    this.#lastScrollLeft = null;
    this.#lastSyncedSlideIndex = null;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;
    this.#bindMediaScrollContainer();

    const requestedIndex = Number(index);
    if (!Number.isFinite(requestedIndex)) return;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const syncSettleMs = options.behavior === 'smooth' ? (isMobile ? 300 : 340) : 120;

    const { thumbnails } = this.refs;
    const targetImage =
      this.#findMediaBySlideIndex(requestedIndex, true) ||
      this.#findMediaBySlideIndex(requestedIndex, false) ||
      this.#getVisibleMedia()[0];
    if (!targetImage) return;
    const targetSlideIndex = this.#getSlideIndexFromElement(targetImage, requestedIndex);
    const targetThumbnail =
      this.#findThumbnailBySlideIndex(targetSlideIndex, true) || this.#findThumbnailBySlideIndex(targetSlideIndex);

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#syncThumbnailSelection(targetSlideIndex);
    this.#suppressScrollSync(syncSettleMs);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });
    requestAnimationFrame(() => this.#scrollThumbnailIntoView(targetSlideIndex, 'instant'));

    if (targetImage) {
      this.#setLoadingStateForMedia(targetImage);
      targetImage.scrollIntoView({
        behavior: options.behavior,
        block: isMobile ? 'center' : 'start',
        inline: isMobile ? 'center' : 'nearest',
      });

      this.#loadHighResolutionImage(targetImage);
      this.#autoplayMedia(targetImage);
      this.#pauseInactiveMedia(targetSlideIndex);
      setTimeout(() => this.handleScroll(), 80);
    }
    this.#lastSyncedSlideIndex = targetSlideIndex;
    this.dispatchEvent(new ZoomMediaSelectedEvent(targetSlideIndex));
    setTimeout(() => this.#syncFromScrollPosition(), syncSettleMs + 40);
  }
  }

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
